// src/services/AuthService.ts - with improved server-side handling
import { API_BASE_URL } from '../config';
import { AuthStateChecker } from '../utils/authStateChecker';


// Define user type
interface User {
  id: string;
  name: string;
  email: string;
  role?: string;
}

// Define auth state
interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  loading: boolean;
}

// Define response types
interface AuthResponse {
  success: boolean;
  message?: string;
  user?: User;
  token?: string;
  error?: string;
}

interface LogoutResponse {
  success: boolean;
  message?: string;
  error?: string;
}

class AuthService {
  private authStateListeners: Function[] = [];
  private isInitialized = false;
  private currentUser: User | null = null;
  private loading = true;
  
  /* constructor() {
    // Initialize on construction
    this.initialize();
  } */

  constructor() {
    // Clean up any corrupted tokens on startup
    this.cleanupCorruptedTokens();
    
    // Initialize on construction
    this.initialize();
  }

  private cleanupCorruptedTokens() {
    const tokenKeys = ['authToken', 'tripperSessionToken', 'sessionToken'];
    
    tokenKeys.forEach(key => {
      const value = localStorage.getItem(key);
      if (value === 'undefined' || value === 'null' || value === 'false') {
        console.warn(`Cleaning up corrupted ${key}`);
        localStorage.removeItem(key);
      }
    });
  }
  
/*   async initialize() {
    this.loading = true;
    this.notifyListeners();
    
    try {
      
      if (!token && localStorage.getItem('tripperUserEmail')) {
        await this.syncAccountStatus();
      }

      // Try to get token from localStorage
      const token = localStorage.getItem('authToken');
      
      if (token) {
        // Validate with server
        const response = await fetch(`${API_BASE_URL}/api/auth/verify`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.user) {
            this.setAuthState(data.user, true);
          } else {
            // Token is invalid according to server
            console.warn('Token verification failed:', data.message || 'Unknown reason');
            this.clearAuthState();
          }
        } else {
          // Server rejected token
          console.warn('Token rejected by server:', response.status, response.statusText);
          this.clearAuthState();
        }
      } else {
        this.clearAuthState();
      }
    } catch (error) {
      console.error('Auth initialization error:', error);
      this.clearAuthState();
    } finally {
      this.loading = false;
      this.isInitialized = true;
      AuthStateChecker.check('initialize');

      this.notifyListeners();
    }
  } */
/*   async initialize() {
    this.loading = true;
    this.notifyListeners();
    
    try {
      // Try to get token from localStorage
      const token = localStorage.getItem('authToken');
      
      if (token) {
        // Validate with server
        const response = await fetch(`${API_BASE_URL}/api/auth/verify`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.user) {
            this.setAuthState(data.user, true);
          } else {
            // Token is invalid according to server
            console.warn('Token verification failed:', data.message || 'Unknown reason');
            this.clearAuthState();
          }
        } else {
          // Server rejected token
          console.warn('Token rejected by server:', response.status, response.statusText);
          this.clearAuthState();
        }
      } else {
        // No token, but check if we have a stored email to sync account status
        const storedEmail = localStorage.getItem('tripperUserEmail');
        
        if (storedEmail) {
          // Add this new code to check account status when logged out
          try {
            const response = await fetch(
              `${API_BASE_URL}/api/auth/check-account?email=${encodeURIComponent(storedEmail)}`,
              { signal: AbortSignal.timeout(5000) } // 5-second timeout
            );
            
            if (response.ok) {
              const data = await response.json();
              if (data.success && data.exists) {
                // User exists in database, update local storage
                localStorage.setItem('tripperHasAccount', 'true');
                localStorage.setItem('tripperUserExists', 'true');
                console.log('Account status synced: User exists');
              } else {
                // User doesn't exist in database
                localStorage.setItem('tripperHasAccount', 'false');
                localStorage.setItem('tripperUserExists', 'false');
                console.log('Account status synced: User does not exist');
              }
            }
          } catch (error) {
            console.warn('Error checking account status:', error);
            // Continue with existing stored values if server check fails
          }
        } else {
          // No token and no email, clear auth state
          this.clearAuthState();
        }
      }
    } catch (error) {
      console.error('Auth initialization error:', error);
      this.clearAuthState();
    } finally {
      this.loading = false;
      this.isInitialized = true;
      AuthStateChecker.check('initialize');

      this.notifyListeners();
    }
  } */
  async initialize() {
    console.log("=== AUTH INITIALIZATION STARTED ===");
    console.log("Initial localStorage state:", this.getLocalStorageState());
    
    this.loading = true;
    this.notifyListeners();
    
    try {
      // Try to get token from localStorage .. Latest Change
      //const token = localStorage.getItem('authToken');

      const token = this.getValidToken();
      console.log("Found authToken:", !!token);
      
      if (token) {
        //console.log("Token exists, verifying with server...");
        try {
          const response = await fetch(`${API_BASE_URL}/api/auth/verify`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          console.log("Token verification response:", response.status, response.statusText);
          
          if (response.ok) {
            const data = await response.json();
            console.log("Token verification data:", data);
            
            if (data.success && data.user) {
              console.log("Token valid, setting auth state with user:", data.user);
              this.setAuthState(data.user, true);
              console.log("Auth state set successfully");
            } else {
              // Token is invalid according to server
              console.warn('Token verification failed:', data.message || 'Unknown reason');
              this.clearAuthState();
              console.log("Auth state cleared due to invalid token");
            }
          } else {
            // Server rejected token
            console.warn('Token rejected by server:', response.status, response.statusText);
            this.clearAuthState();
            console.log("Auth state cleared due to server rejection");
          }
        } catch (error) {
          console.error("Error during token verification:", error);
          this.clearAuthState();
          console.log("Auth state cleared due to verification error");
        }
      } else {
        // No token, but check if we have a stored email to sync account status
        const storedEmail = localStorage.getItem('tripperUserEmail');
        console.log("No token found. Stored email:", storedEmail);
        
        if (storedEmail) {
          console.log("Email found in localStorage, checking account status with server...");
          // Add this new code to check account status when logged out
          try {
            const accountCheckUrl = `${API_BASE_URL}/api/auth/check-account?email=${encodeURIComponent(storedEmail)}`;
            console.log("Checking account with URL:", accountCheckUrl);
            
            const response = await fetch(accountCheckUrl, 
              { signal: AbortSignal.timeout(5000) } // 5-second timeout
            );
            
            console.log("Account check response:", response.status, response.statusText);
            
            if (response.ok) {
              const data = await response.json();
              console.log("Account check data:", data);
              
              if (data.success && data.exists) {
                // User exists in database, update local storage
                console.log("Server confirms account exists, updating localStorage");
                localStorage.setItem('tripperHasAccount', 'true');
                localStorage.setItem('tripperUserExists', 'true');
                console.log('Account status synced: User exists');
                console.log("Updated localStorage state:", this.getLocalStorageState());
              } else {
                // User doesn't exist in database
                console.log("Server says account does not exist, updating localStorage");
                localStorage.setItem('tripperHasAccount', 'false');
                localStorage.setItem('tripperUserExists', 'false');
                console.log('Account status synced: User does not exist');
                console.log("Updated localStorage state:", this.getLocalStorageState());
              }
            } else {
              console.warn("Account check failed:", response.status, response.statusText);
              // On failure, we'll keep existing localStorage values
              console.log("Keeping existing localStorage values");
            }
          } catch (error) {
            console.warn('Error checking account status:', error);
            console.log("Error details:", {
              name: error.name,
              message: error.message,
              stack: error.stack
            });
            // Continue with existing stored values if server check fails
            console.log("Due to error, keeping existing localStorage values");
          }
        } else {
          // No token and no email, clear auth state
          console.log("No token and no email found, clearing auth state");
          this.clearAuthState();
        }
      }
    } catch (error) {
      console.error('Auth initialization error:', error);
      console.log("Error details:", {
        name: error.name,
        message: error.message,
        stack: error.stack
      });
      this.clearAuthState();
      console.log("Auth state cleared due to initialization error");
    } finally {
      this.loading = false;
      this.isInitialized = true;
      
      console.log("Final localStorage state:", this.getLocalStorageState());
      console.log("=== AUTH INITIALIZATION COMPLETED ===");
      
      AuthStateChecker.check('initialize');
      this.notifyListeners();
    }
  }

  // Add this helper method to AuthService class
  private isValidToken(token: any): boolean {
    if (!token) return false;
    if (typeof token !== 'string') return false;
    if (token === 'undefined' || token === 'null' || token === 'false') return false;
    if (token.length < 10) return false; // Too short to be valid
    
    // Additional JWT validation if needed
    if (token.includes('.')) {
      const parts = token.split('.');
      if (parts.length !== 3) return false;
    }
    
    return true;
  }

  // Add safe token getter
  private getValidToken(): string | null {
    const token = localStorage.getItem('authToken');
    //const token = authService.getAuthToken();

    if (!this.isValidToken(token)) {
      // Clean up invalid token
      localStorage.removeItem('authToken');
      return null;
    }
    
    return token;
  }
  
  // Helper function to get all auth-related localStorage state
  private getLocalStorageState() {
    return {
      tripperAuthenticated: localStorage.getItem('tripperAuthenticated'),
      tripperHasAccount: localStorage.getItem('tripperHasAccount'),
      tripperUserEmail: localStorage.getItem('tripperUserEmail'),
      tripperUserName: localStorage.getItem('tripperUserName'),
      tripperUserExists: localStorage.getItem('tripperUserExists'),
      tripperUserId: localStorage.getItem('tripperUserId'),
      tripperLoggedOut: localStorage.getItem('tripperLoggedOut'),
      tripperCodeValidated: localStorage.getItem('tripperCodeValidated'),
      authToken: localStorage.getItem('authToken') ? '[PRESENT]' : null,
      tripperSessionToken: localStorage.getItem('tripperSessionToken') ? '[PRESENT]' : null
    };
  }    
    
  async syncAccountStatus() {
    const email = localStorage.getItem('tripperUserEmail');
    if (!email) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/check-account?email=${encodeURIComponent(email)}`);
      if (response.ok) {
        const data = await response.json();
        if (data.exists) {
          localStorage.setItem('tripperHasAccount', 'true');
          localStorage.setItem('tripperUserExists', 'true');
        }
      }
    } catch (error) {
      console.error('Error syncing account status:', error);
    }
  }
  
  private setAuthState(user: User, isAuthenticated: boolean) {
    this.currentUser = user;
    
    if (isAuthenticated) {
      localStorage.setItem('tripperAuthenticated', 'true');
      // Store minimal user info
      if (user) {
        localStorage.setItem('tripperUserEmail', user.email);
        localStorage.setItem('tripperUserName', user.name || '');
        localStorage.setItem('tripperHasAccount', 'true');
        localStorage.setItem('tripperUserExists', 'true');
        localStorage.setItem('tripperUserId', user.id);
      }
    }
    
    this.notifyListeners();
  }
  
  /* private clearAuthState() {
    this.currentUser = null;
    
    // Clear auth flags but keep account info
    localStorage.removeItem('tripperAuthenticated');
    localStorage.removeItem('authToken');
    localStorage.removeItem('tripperSessionToken');
    
    // Set logout flag
    localStorage.setItem('tripperLoggedOut', 'true');
    
    this.notifyListeners();
  } */
  private clearAuthState() {
    // Check if this is a completely new user
    const isNewUser = !localStorage.getItem('tripperUserEmail') && 
                      !localStorage.getItem('tripperHasAccount') &&
                      !localStorage.getItem('tripperAuthenticated');
    
    this.currentUser = null;
    
    // Clear auth flags
    localStorage.removeItem('tripperAuthenticated');
    localStorage.removeItem('authToken');
    localStorage.removeItem('tripperSessionToken');
    
    // Set logout flag ONLY if this is not a new user
    if (!isNewUser) {
      localStorage.setItem('tripperLoggedOut', 'true');
    }
    
    this.notifyListeners();
  }
  
  private notifyListeners() {
    this.authStateListeners.forEach(listener => listener({
      user: this.currentUser,
      isAuthenticated: !!this.currentUser,
      loading: this.loading
    }));
  }

  // Add these public methods to AuthService class
  public getAuthToken(): string | null {
    return this.getValidToken(); // Uses the private validation method
  }

  public hasValidToken(): boolean {
    return this.isValidToken(this.getAuthToken());
  }
  
  /**
   * Complete login process with server verification first - FIXED
   */
  async login(email: string, password: string, sessionToken?: string): Promise<AuthResponse> {
    
    // At start of login function
    /* const originalWindowReload = window.location.reload;
    window.location.reload = function() {
      console.trace("RELOAD CALLED FROM:");
      return originalWindowReload.apply(this, arguments);
    }; */

    /* window.addEventListener('beforeunload', function(e) {
      console.log('Page is about to unload/reload');
      console.trace('Reload stack trace:');
      
      // The following is needed to show the console message before unload
      // But we don't want to actually block the navigation
      setTimeout(() => {}, 100000);
    }); */

    console.log("AuthService.login called - NOT REFRESHING PAGE");
    this.loading = true;
    this.notifyListeners();
    
    try {
      // Prepare login data with browser info for security
      const loginData = { 
        email, 
        password,
        sessionData: {
          browserInfo: {
            userAgent: navigator.userAgent,
            language: navigator.language,
            platform: navigator.platform,
            screenResolution: `${window.screen.width}x${window.screen.height}`,
          },
          timestamp: Date.now(),
        }
      };
      
      if (sessionToken) {
        Object.assign(loginData, { sessionToken });
      }
      
      console.log(`Attempting login for: ${email} with server at ${API_BASE_URL}`);
      
      // Make server request FIRST - client state only updates if server accepts
      const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(loginData),
        // Add timeout to prevent hanging
        signal: AbortSignal.timeout(10000) // 10-second timeout
      });

      // Parse response
      const result = await response.json();

      // CRITICAL FIX: Handle server errors based on HTTP status code
      if (!response.ok) {
        console.error('Server rejected login:', result.message || response.statusText);
        
        // On 401 Unauthorized - Set explicit failed login flag to override welcome back screen
        if (response.status === 401) {
          localStorage.setItem('tripperLoginFailed', 'true');
          
          // Make sure we clear any stale auth flags that might interfere with UI state
          localStorage.removeItem('tripperAuthenticated');
        }
        
        return {
          success: false,
          message: result.message || 'Login failed: server rejected credentials',
          error: result.error || response.statusText
        };
      }

      // Server accepted login
      if (result.success && result.token && result.user) {

        // Validate token before storing
        if (!this.isValidToken(result.token)) {
          console.error('Server returned invalid token format');
          return {
            success: false,
            message: 'Invalid token received from server'
          };
        }
        // Clear any previous login failure flag
        localStorage.removeItem('tripperLoginFailed');
        
        // Store token securely
        localStorage.setItem('authToken', result.token);
        
        // Set auth state and notify components
        this.setAuthState(result.user, true);
        
        // Log session data for security audit
        await this.logSessionData(result.user.id);
        
        // Remove any logout flag
        localStorage.removeItem('tripperLoggedOut');
        
        console.log('Login successful, server authentication complete');
        
        // Dispatch auth event
        window.dispatchEvent(new Event('tripperAuthenticated'));
        
        return {
          success: true,
          user: result.user,
          token: result.token,
          message: 'Login successful'
        };
      } else {
        // Unexpected response format
        console.error('Unexpected server response format:', result);
        
        // Set login failed flag to be safe
        localStorage.setItem('tripperLoginFailed', 'true');
        
        return {
          success: false,
          message: 'Login failed: unexpected server response',
          error: 'Invalid server response format'
        };
      }
    } catch (error) {
      // Network errors, timeouts, etc.
      console.error('Login error:', error);
      
      // Set login failed flag for network errors too
      localStorage.setItem('tripperLoginFailed', 'true');
      
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Login failed: network error',
        error: error instanceof Error ? error.toString() : 'Unknown error'
      };
    } finally {
      this.loading = false;
      this.notifyListeners();
    }
  }
  
/**
 * Handle social authentication providers with better error handling
 */
  async loginWithProvider(provider: string) {
    // Capture current user state to detect if they're trying to link accounts
    const currentEmail = localStorage.getItem('tripperUserEmail');
    const hasAccount = localStorage.getItem('tripperHasAccount') === 'true';
    
    // Store useful data in session storage for after redirect
    sessionStorage.setItem('socialAuthProvider', provider);
    sessionStorage.setItem('socialAuthAttemptTime', Date.now().toString());
    
    if (currentEmail) {
      sessionStorage.setItem('socialAuthAttemptEmail', currentEmail);
    }
    
    if (hasAccount) {
      sessionStorage.setItem('socialAuthHasExistingAccount', 'true');
    }
    
    // Preserve code validation state
    if (localStorage.getItem('tripperCodeValidated') === 'true') {
      localStorage.setItem('tripperCodeValidatedBeforeSocial', 'true');
    }
    
    // Log the attempt
    console.log(`Initiating ${provider} authentication redirect`);
    
    try {
      // Build the redirect URL with additional parameters to help with error handling
      let redirectUrl = `${API_BASE_URL}/api/auth/${provider}`;
      
      // Add query parameters if needed
      const params = new URLSearchParams();
      
      if (currentEmail) {
        params.append('attempted_email', currentEmail);
      }
      
      if (hasAccount) {
        params.append('has_account', 'true');
      }
      
      // Add querystring if we have parameters
      if (params.toString()) {
        redirectUrl += `?${params.toString()}`;
      }
      
      // Redirect to provider auth - this will navigate away from current page
      window.location.href = redirectUrl;
      
      return true;
    } catch (error) {
      console.error(`Error initiating ${provider} login:`, error);
      throw new Error(`Failed to initiate ${provider} authentication`);
    }
  }
/*   async loginWithProvider(provider: string) {
    // Store provider in session storage for after redirect
    sessionStorage.setItem('socialAuthProvider', provider);
    
    // Preserve code validation state
    if (localStorage.getItem('tripperCodeValidated') === 'true') {
      localStorage.setItem('tripperCodeValidatedBeforeSocial', 'true');
    }
    
    // Log the attempt
    console.log(`Initiating ${provider} authentication redirect`);
    
    // Redirect to provider auth - this will navigate away from current page
    window.location.href = `${API_BASE_URL}/api/auth/${provider}`;
  } */
  
  /**
   * Comprehensive logout with server-first approach
   */
/*   async logout(): Promise<LogoutResponse> {
    this.loading = true;
    this.notifyListeners();
    
    try {

      // 1. CRITICAL: Preserve account identity data before doing anything else
      const userEmail = localStorage.getItem('tripperUserEmail');
      const hasAccount = localStorage.getItem('tripperHasAccount');
      const userExists = localStorage.getItem('tripperUserExists');
      console.log("Preserving identity before logout:", { userEmail, hasAccount, userExists });

      // Lock UI to login immediately to prevent unwanted transitions
      sessionStorage.setItem('tripperLogoutTime', Date.now().toString());
      localStorage.setItem('tripperLoggedOut', 'true');
      
      // UI event - this happens first for user experience
      window.dispatchEvent(new CustomEvent('tripperLogout'));
      
      // Get current token for server request
      const token = localStorage.getItem('authToken');
      let serverLogoutSuccess = true; // Assume success if no token

      // Perform server-side logout if we have a token
      if (token) {
        try {
          console.log('Sending logout request to server');
          
          // Make server request with timeout
          const response = await fetch(`${API_BASE_URL}/api/auth/logout`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            // Add some minimal data for audit
            body: JSON.stringify({
              timestamp: Date.now(),
              userId: this.currentUser?.id || 'unknown'
            }),
            signal: AbortSignal.timeout(5000) // 5-second timeout
          });

          // Check server response
          if (response.ok) {
            const result = await response.json();
            if (result.success) {
              console.log('Server confirmed logout successful');
              serverLogoutSuccess = true;
            } else {
              console.warn('Server reported logout issue:', result.message);
              serverLogoutSuccess = false;
            }
          } else {
            console.warn('Server logout failed with status:', response.status, response.statusText);
            serverLogoutSuccess = false;
          }
        } catch (error) {
          // Handle network errors, timeouts, etc.
          console.error('Server logout communication error:', error);
          serverLogoutSuccess = false;
        }
      }
      
      // ALWAYS proceed with client-side logout, even if server failed
      // This prevents users from being stuck in a logged-in state

      // Clear all auth tokens from storage
      localStorage.removeItem('tripperAuthenticated');
      localStorage.removeItem('authToken');
      localStorage.removeItem('tripperSessionToken');
      localStorage.setItem('tripperLoggedOut', 'true');


      // Clear session-specific items
      sessionStorage.removeItem('socialAuthSuccess');
      sessionStorage.removeItem('socialAuthProvider');
      sessionStorage.removeItem('socialAuthEmail');
      sessionStorage.removeItem('tripperAccountCreated');
      
      // 4. RESTORE identity info
      if (userEmail) {
        console.log("Restoring email after logout:", userEmail);
        localStorage.setItem('tripperUserEmail', userEmail);
      }
      if (hasAccount === 'true') {
        console.log("Restoring hasAccount after logout");
        localStorage.setItem('tripperHasAccount', 'true');
      }
      if (userExists === 'true') {
        console.log("Restoring userExists after logout");
        localStorage.setItem('tripperUserExists', 'true');
      }

      // Reset internal state
      this.currentUser = null;
      
      // Notify all listeners
      this.notifyListeners();
      
      safeAuthCheck('logout');

      // Return combined result
      return {
        success: true, // Client logout always succeeds
        message: serverLogoutSuccess 
          ? 'Logout completed successfully on server and client' 
          : 'Logout completed on client, but server confirmation failed',
      };
    } catch (error) {
      console.error('Logout error:', error);
      return {
        success: false,
        message: 'Logout failed with unexpected error',
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    } finally {
      this.loading = false;
      this.notifyListeners();
    }
  } */
  /**
 * Comprehensive logout with server-first approach that preserves account identity
 */
  async logout(): Promise<LogoutResponse> {
    console.log('=== LOGOUT PROCESS STARTED ===');
    this.loading = true;
    this.notifyListeners();
    
    try {
      // 1. CRITICAL: Preserve account identity data before doing anything else
      const userEmail = localStorage.getItem('tripperUserEmail');
      const hasAccount = localStorage.getItem('tripperHasAccount');
      const userExists = localStorage.getItem('tripperUserExists');
      const userName = localStorage.getItem('tripperUserName');
      
      console.log("Preserving identity before logout:", { 
        userEmail, 
        hasAccount, 
        userExists,
        userName
      });
      
      // 2. Lock UI to login immediately to prevent unwanted transitions
      sessionStorage.setItem('tripperLogoutTime', Date.now().toString());
      localStorage.setItem('tripperLoggedOut', 'true');
      
      // 3. UI event - this happens first for user experience
      window.dispatchEvent(new CustomEvent('tripperLogout'));
      
      // 4. Get current token for server request
      //const token = localStorage.getItem('authToken');
      const token = authService.getAuthToken();
      let serverLogoutSuccess = true; // Assume success if no token

      // 5. Perform server-side logout if we have a token
      if (token) {
        try {
          console.log('Sending logout request to server');
          
          // Make server request with timeout
          const response = await fetch(`${API_BASE_URL}/api/auth/logout`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            // Add some minimal data for audit
            body: JSON.stringify({
              timestamp: Date.now(),
              userId: this.currentUser?.id || 'unknown'
            }),
            signal: AbortSignal.timeout(5000) // 5-second timeout
          });

          // Check server response
          if (response.ok) {
            const result = await response.json();
            if (result.success) {
              console.log('Server confirmed logout successful');
              serverLogoutSuccess = true;
            } else {
              console.warn('Server reported logout issue:', result.message);
              serverLogoutSuccess = false;
            }
          } else {
            console.warn('Server logout failed with status:', response.status, response.statusText);
            serverLogoutSuccess = false;
          }
        } catch (error) {
          // Handle network errors, timeouts, etc.
          console.error('Server logout communication error:', error);
          serverLogoutSuccess = false;
        }
      }
      
      // 6. Clear ONLY auth tokens from storage (not identity information)
      console.log('Clearing only authentication tokens...');
      localStorage.removeItem('tripperAuthenticated');
      localStorage.removeItem('authToken');
      localStorage.removeItem('tripperSessionToken');
      
      // 7. Clear session-specific items
      sessionStorage.removeItem('socialAuthSuccess');
      sessionStorage.removeItem('socialAuthProvider');
      sessionStorage.removeItem('socialAuthEmail');
      sessionStorage.removeItem('tripperAccountCreated');
      
      // 8. RESTORE identity info - CRITICAL STEP
      console.log('Restoring identity information after token clearing...');
      if (userEmail) {
        console.log("Restoring email:", userEmail);
        localStorage.setItem('tripperUserEmail', userEmail);
      }
      
      if (hasAccount === 'true') {
        console.log("Restoring hasAccount flag");
        localStorage.setItem('tripperHasAccount', 'true');
      }
      
      if (userExists === 'true') {
        console.log("Restoring userExists flag");
        localStorage.setItem('tripperUserExists', 'true');
      }
      
      if (userName) {
        console.log("Restoring userName:", userName);
        localStorage.setItem('tripperUserName', userName);
      }
      
      // 9. Reset internal state
      this.currentUser = null;
      
      // 10. Notify all listeners
      this.notifyListeners();
      
      console.log('Final localStorage state after logout:', this.getLocalStorageState());
      console.log('=== LOGOUT PROCESS COMPLETED ===');
      
      safeAuthCheck('logout');

      // Return combined result
      return {
        success: true, // Client logout always succeeds
        message: serverLogoutSuccess 
          ? 'Logout completed successfully on server and client' 
          : 'Logout completed on client, but server confirmation failed',
      };
    } catch (error) {
      console.error('Logout error:', error);
      return {
        success: false,
        message: 'Logout failed with unexpected error',
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    } finally {
      this.loading = false;
      this.notifyListeners();
    }
  }


  /**
   * Delete account completely (for testing purposes)
   * This will remove the account from the database and clear all local state
   */
  /* async deleteAccount(): Promise<{success: boolean, message: string}> {
    try {
      this.loading = true;
      this.notifyListeners();
      
      // Get current user ID and token
      const userId = this.currentUser?.id || localStorage.getItem('tripperUserId');
      const token = localStorage.getItem('authToken');
      
      if (!userId || !token) {
        return {
          success: false,
          message: 'No active user to delete'
        };
      }
      
      // Send delete request to server
      const response = await fetch(`${API_BASE_URL}/api/users/${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        signal: AbortSignal.timeout(5000) // 5-second timeout
      });
      
      // Even if the server response fails, we'll clear the local state
      
      // Clear ALL localStorage items
      const itemsToClear = [
        'tripperAuthenticated',
        'tripperHasAccount',
        'tripperUserEmail',
        'tripperUserName',
        'tripperUserId',
        'tripperSessionToken',
        'tripperCodeValidated',
        'tripperLoggedOut',
        'tripperUserExists',
        'tripperInviteSent',
        'tripperAccountJustCreated',
        'tripperCodeValidatedBeforeSocial',
        'authToken',
        'userData',
        'rememberedEmail'
      ];
      
      itemsToClear.forEach(key => localStorage.removeItem(key));
      
      // Clear session storage
      sessionStorage.clear();
      
      // Reset internal state
      this.currentUser = null;
      
      // Dispatch logout event
      window.dispatchEvent(new CustomEvent('tripperLogout'));
      window.dispatchEvent(new CustomEvent('accountDeleted'));
      
      // If the server response was successful
      if (response.ok) {
        const result = await response.json();
        return {
          success: true,
          message: result.message || 'Account successfully deleted'
        };
      } else {
        // Still return success for UI purposes since we've deleted all local data
        return {
          success: true,
          message: 'Local account data cleared (server could not confirm deletion)'
        };
      }
    } catch (error) {
      console.error('Error deleting account:', error);
      
      // Clear state regardless of error
      localStorage.clear();
      sessionStorage.clear();
      this.currentUser = null;
      
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Unknown error deleting account'
      };
    } finally {
      this.loading = false;
      this.notifyListeners();
    }
  } */
  /**
   * Delete account completely (for testing purposes)
   * This will remove the account from the database and clear all local state
   */
  async deleteAccount(): Promise<{success: boolean, message: string}> {
    try {
      this.loading = true;
      this.notifyListeners();
      
      // Get current user ID - check all possible sources
      const userId = 
        this.currentUser?.id || 
        localStorage.getItem('tripperUserId') ||
        localStorage.getItem('userId');
      
      // Get token - check all possible storage locations
      const token = 
        localStorage.getItem('authToken') ||
        localStorage.getItem('token');
      
      console.log("Delete account debug:", { 
        currentUserId: this.currentUser?.id,
        localStorageUserId: localStorage.getItem('tripperUserId'),
        plainUserId: localStorage.getItem('userId'),
        hasToken: !!token,
        allLocalStorage: Object.keys(localStorage)
      });
      
      // If we can't find a user ID but have a token, get the user profile from the server
      if (!userId && token) {
        try {
          const profileResponse = await fetch(`${API_BASE_URL}/api/auth/me`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            signal: AbortSignal.timeout(5000)
          });
          
          if (profileResponse.ok) {
            const profile = await profileResponse.json();
            if (profile.success && profile.user && profile.user.id) {
              console.log("Retrieved user ID from profile:", profile.user.id);
              localStorage.setItem('tripperUserId', profile.user.id);
            }
          }
        } catch (error) {
          console.error("Error retrieving user profile:", error);
        }
      }
      
      // Final check for user ID
      const finalUserId = 
        this.currentUser?.id || 
        localStorage.getItem('tripperUserId') ||
        localStorage.getItem('userId');
      
      // If we still don't have what we need, proceed with client-side cleanup only
      if (!finalUserId || !token) {
        console.warn("Missing user ID or token for server deletion, proceeding with client-side cleanup only");
        
        // Force client-side logout
        localStorage.clear();
        sessionStorage.clear();
        this.currentUser = null;
        
        // Dispatch events
        window.dispatchEvent(new CustomEvent('tripperLogout'));
        window.dispatchEvent(new CustomEvent('accountDeleted'));
        
        return {
          success: true,
          message: 'Account data cleared locally (could not identify user on server)'
        };
      }
      
      // Send delete request to server
      console.log(`Sending delete request for user ID: ${finalUserId}`);
      const response = await fetch(`${API_BASE_URL}/api/users/${finalUserId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        signal: AbortSignal.timeout(5000) // 5-second timeout
      });
      
      // Clear ALL localStorage items
      const itemsToClear = [
        'tripperAuthenticated',
        'tripperHasAccount',
        'tripperUserEmail',
        'tripperUserName',
        'tripperUserId',
        'tripperSessionToken',
        'tripperCodeValidated',
        'tripperLoggedOut',
        'tripperUserExists',
        'tripperInviteSent',
        'tripperAccountJustCreated',
        'tripperCodeValidatedBeforeSocial',
        'authToken',
        'userData',
        'rememberedEmail'
      ];
      
      itemsToClear.forEach(key => localStorage.removeItem(key));
      
      // Clear session storage
      sessionStorage.clear();
      
      // Reset internal state
      this.currentUser = null;
      
      // Dispatch logout event
      window.dispatchEvent(new CustomEvent('tripperLogout'));
      window.dispatchEvent(new CustomEvent('accountDeleted'));
      
      // If the server response was successful
      if (response.ok) {
        const result = await response.json();
        safeAuthCheck('delete_account', false); // false because token is already invalidated

        return {
          success: true,
          message: result.message || 'Account successfully deleted'
        };
      } else {
        safeAuthCheck('delete_account', false); // false because token is already invalidated

        // Still return success for UI purposes since we've deleted all local data
        return {
          success: true,
          message: `Local account data cleared (server returned ${response.status}: ${response.statusText})`
        };
      }
    } catch (error) {
      console.error('Error deleting account:', error);
      
      // Clear state regardless of error
      localStorage.clear();
      sessionStorage.clear();
      this.currentUser = null;
      
      return {
        success: true, // Still return success since we cleared local data
        message: `Account data cleared locally. Server error: ${error instanceof Error ? error.message : 'Unknown error'}`
      };
    } finally {
      this.loading = false;
      this.notifyListeners();
    }
  }
  /**
   * Log session data for security audit
   */
  private async logSessionData(userId: string) {
    try {
      const sessionData = {
        browserInfo: {
          userAgent: navigator.userAgent,
          language: navigator.language,
          platform: navigator.platform,
          screenResolution: `${window.screen.width}x${window.screen.height}`,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          colorDepth: window.screen.colorDepth,
        },
        timestamp: Date.now(),
        userId: userId
      };
      
      // Make server request
      const response = await fetch(`${API_BASE_URL}/api/auth/session-log`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
        },
        body: JSON.stringify({
          userId,
          sessionData,
        }),
        signal: AbortSignal.timeout(5000) // 5-second timeout
      });
      
      if (!response.ok) {
        console.warn('Session logging failed:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Failed to log session data:', error);
      // Non-critical error, so just log it
    }
  }
  
  /**
   * Observer pattern for auth state changes
   */
  onAuthStateChanged(listener: Function) {
    this.authStateListeners.push(listener);
    
    // Call immediately with current state if initialized
    if (this.isInitialized) {
      listener({
        user: this.currentUser,
        isAuthenticated: !!this.currentUser,
        loading: this.loading
      });
    }
    
    // Return unsubscribe function
    return () => {
      this.authStateListeners = this.authStateListeners.filter(l => l !== listener);
    };
  }
  
  // Get current auth state
  getCurrentUser() {
    return this.currentUser;
  }
  
  isAuthenticated() {
    return !!this.currentUser;
  }
  
  isLoading() {
    return this.loading;
  }
  
  /**
   * Verify current auth state with server
   */
  async checkAuth(): Promise<boolean> {
    if (!this.isInitialized) {
      await this.initialize();
    }
    
    // If we have no token, we're definitely not authenticated
    //const token = localStorage.getItem('authToken');
    const token = authService.getAuthToken();
    if (!token) {
      return false;
    }
    
    // We have a token, verify with server
    try {
      this.loading = true;
      this.notifyListeners();
      
      const response = await fetch(`${API_BASE_URL}/api/auth/verify`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        signal: AbortSignal.timeout(5000) // 5-second timeout
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.user) {
          // Update user data in case it changed on server
          this.setAuthState(data.user, true);
          safeAuthCheck('check_auth');

          return true;
        }
      }
      
      // If we reach here, token is invalid
      this.clearAuthState();
      safeAuthCheck('check_auth');

      return false;
    } catch (error) {
      console.error('Auth check failed:', error);
      // On network error, we keep existing state
      // This prevents logging out users due to temporary network issues
      return this.isAuthenticated();
    } finally {
      this.loading = false;
      this.notifyListeners();
    }
  }
}
/**
 * Helper function to safely run auth state checks
 */
function safeAuthCheck(operation: string, includeServer = true) {
  try {
    // Only run in development
    if (process.env.NODE_ENV !== 'production' && typeof AuthStateChecker !== 'undefined') {
      // Use dynamic import to prevent issues if the module isn't available
      import('../utils/authStateChecker').then(module => {
        const { AuthStateChecker } = module;
        AuthStateChecker.check(operation, includeServer).catch(err => 
          console.warn(`Auth check error in ${operation}:`, err)
        );
      }).catch(err => {
        console.warn('Could not load AuthStateChecker:', err);
      });
    }
  } catch (error) {
    console.warn('Error running auth check:', error);
  }
}


// Create singleton instance
export const authService = new AuthService();